/**
 * Which library clips the user has ticked — shared by the LIBRARY list (the checkboxes on the rows,
 * `libraryPanel.ts`) and the actions those ticks feed (`libraryFilesPanel.ts`, right column).
 *
 * Module-level and deliberately NOT in the store: a selection is a gesture, not a document. It is
 * never persisted, it survives the panel rebuilds that happen between two clicks, and it is cleared
 * when the left panel goes away — leaving LIBRARY forgets it, exactly like the panel-local `Set`
 * this replaces. A panel-local `Set` is what stopped working the moment two panels needed the same
 * answer.
 *
 * NOT to be confused with `state.library.selectedClipIds`: that is the EXPORT embed list ("which
 * clips travel inside the character GLB"), a different question, persisted with the project.
 */

const selected = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  // Iterate a copy: a listener that unsubscribes while it runs must not make its neighbour skip.
  for (const listener of [...listeners]) listener();
}

export interface LibrarySelection {
  has(id: string): boolean;
  toggle(id: string): void;
  /** Tick (or untick) a whole group — one notification for the batch, not one per id. */
  setMany(ids: readonly string[], on: boolean): void;
  clear(): void;
  /** Ticked ids in the order they were ticked; callers prune them against the live library. */
  ids(): string[];
  subscribe(fn: () => void): () => void;
}

export const librarySelection: LibrarySelection = {
  has(id) {
    return selected.has(id);
  },

  toggle(id) {
    if (!selected.delete(id)) selected.add(id);
    emit();
  },

  setMany(ids, on) {
    let changed = false;
    for (const id of ids) {
      if (on) {
        if (!selected.has(id)) {
          selected.add(id);
          changed = true;
        }
      } else if (selected.delete(id)) {
        changed = true;
      }
    }
    // Silence when nothing moved: a rebuild per no-op click is a rebuild that can eat a pointer.
    if (changed) emit();
  },

  clear() {
    if (selected.size === 0) return;
    selected.clear();
    emit();
  },

  ids() {
    return [...selected];
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};
